import React, { useState } from "react";
import Icon from "@mdi/react";
import { mdiCheckCircleOutline, mdiAlertCircleOutline, mdiWhatsapp, mdiSend } from "@mdi/js";
import { services } from "../content/services";
import { sendContactMessage, buildWhatsAppLink } from "../lib/contact";
import { useSite } from "../theme/ThemeProvider";

const initial = { name: "", phone: "", email: "", service: "", vehicle: "", message: "", website: "" };

const ContactForm = ({ defaultService = "" }) => {
  const site = useSite();
  const [form, setForm] = useState({ ...initial, service: defaultService });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error | fallback

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Adınızı yazın.";
    if (!/^[0-9+\s()-]{10,}$/.test(form.phone.trim())) e.phone = "Geçerli bir telefon numarası yazın.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Geçerli bir e-posta yazın.";
    if (form.message.trim().length < 5) e.message = "Kısaca ne için yazdığınızı belirtin.";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (form.website) return; // honeypot
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setStatus("sending");
    try {
      await sendContactMessage({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        service: form.service,
        vehicle: form.vehicle.trim(),
        message: form.message.trim(),
        page: window.location.href,
      });
      setStatus("sent");
      setForm({ ...initial });
    } catch (err) {
      setStatus(err.code === "no-endpoint" ? "fallback" : "error");
    }
  };

  const waLink = buildWhatsAppLink(site.whatsapp, {
    name: form.name,
    phone: form.phone,
    service: form.service,
    message: [form.vehicle && `Araç: ${form.vehicle}`, form.message].filter(Boolean).join(" — "),
  });

  if (status === "sent") {
    return (
      <div className="alert alert--success" role="status">
        <Icon path={mdiCheckCircleOutline} />
        <div>
          <strong>Mesajınız iletildi.</strong> En kısa sürede sizi arayacağız. Acil ise {" "}
          <a href={`tel:${site.phoneHref}`}>{site.phone}</a> numarasından bize ulaşabilirsiniz.
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="form-grid">
      <div className={`field${errors.name ? " field--error" : ""}`}>
        <label htmlFor="c-name">Ad Soyad *</label>
        <input id="c-name" value={form.name} onChange={set("name")} autoComplete="name" placeholder="Adınız" />
        {errors.name && <span className="error">{errors.name}</span>}
      </div>
      <div className={`field${errors.phone ? " field--error" : ""}`}>
        <label htmlFor="c-phone">Telefon *</label>
        <input id="c-phone" value={form.phone} onChange={set("phone")} type="tel" autoComplete="tel" placeholder="05xx xxx xx xx" />
        {errors.phone && <span className="error">{errors.phone}</span>}
      </div>
      <div className={`field${errors.email ? " field--error" : ""}`}>
        <label htmlFor="c-email">E-posta *</label>
        <input id="c-email" value={form.email} onChange={set("email")} type="email" autoComplete="email" placeholder="ornek@eposta.com" />
        {errors.email && <span className="error">{errors.email}</span>}
      </div>
      <div className="field">
        <label htmlFor="c-service">Hizmet</label>
        <select id="c-service" value={form.service} onChange={set("service")}>
          <option value="">Seçin</option>
          {services.map((s) => (
            <option key={s.slug} value={s.title}>
              {s.title}
            </option>
          ))}
          <option value="Diğer">Diğer</option>
        </select>
      </div>
      <div className="field field--full">
        <label htmlFor="c-vehicle">Araç marka / model / yıl</label>
        <input id="c-vehicle" value={form.vehicle} onChange={set("vehicle")} placeholder="Örn. Fiat Egea 1.4 2019" />
      </div>
      <div className={`field field--full${errors.message ? " field--error" : ""}`}>
        <label htmlFor="c-message">Mesajınız *</label>
        <textarea id="c-message" value={form.message} onChange={set("message")} placeholder="Ne için yazıyorsunuz? Uygun olduğunuz gün/saat varsa ekleyin." />
        {errors.message && <span className="error">{errors.message}</span>}
      </div>
      <div className="field" style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
        <label htmlFor="c-website">Web sitesi</label>
        <input id="c-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
      </div>

      {status === "error" && (
        <div className="alert alert--error field--full">
          <Icon path={mdiAlertCircleOutline} />
          <div>
            Mesaj gönderilemedi. Lütfen tekrar deneyin ya da{" "}
            <a href={waLink} target="_blank" rel="noreferrer">WhatsApp</a> üzerinden yazın.
          </div>
        </div>
      )}
      {status === "fallback" && (
        <div className="alert alert--info field--full">
          <Icon path={mdiAlertCircleOutline} />
          <div>
            E-posta gönderimi henüz aktif değil. Mesajınızı tek tıkla WhatsApp'tan iletebilirsiniz:{" "}
            <a href={waLink} target="_blank" rel="noreferrer">WhatsApp ile gönder</a>
          </div>
        </div>
      )}

      <div className="field--full" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button type="submit" className="btn btn--primary btn--lg" disabled={status === "sending"}>
          <Icon path={mdiSend} /> {status === "sending" ? "Gönderiliyor…" : "Gönder"}
        </button>
        <a href={waLink} target="_blank" rel="noreferrer" className="btn btn--whatsapp btn--lg">
          <Icon path={mdiWhatsapp} /> WhatsApp'tan yaz
        </a>
      </div>
    </form>
  );
};

export default ContactForm;
